import { useCallback, useEffect, useRef, useState } from 'react';
import DashboardService, { DateRange, LocationData, SalesTier } from '../services/DashboardService';
import {
  calculateBaselineAverages,
  storeBaseline,
  getStoredBaseline,
  BaselineData,
  BaselineChanges,
  TierBaselineData,
} from '../services/baselineService';
import { formatDisplayDate } from '../utils/formatters';
import { useRetry } from './useRetry';

interface UseBaselineProps {
  dateRanges: DateRange[];
  selectedDateRange: DateRange | null;
  viewMode: 'weekly' | 'quarterly';
  sortBy: string;
}

/**
 * Hook for baseline calculations and management
 */
export const useBaseline = ({
  dateRanges,
  selectedDateRange,
  viewMode,
  sortBy,
}: UseBaselineProps) => {
  const { fetchWithRetry } = useRetry();
  const [baselineDataByTier, setBaselineDataByTier] = useState<Record<string, TierBaselineData>>({});
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineChangeLoading, setBaselineChangeLoading] = useState(false);
  const latestBaselineRequestRef = useRef(0);

  // Get the index of a week in the dateRanges array
  const getWeekIndex = useCallback(
    (startDate: string): number => {
      return dateRanges.findIndex((range) => range.startDate === startDate);
    },
    [dateRanges]
  );

  // Get the baseline week for a given week index
  const getBaselineWeekIndex = useCallback(
    (weekIndex: number): number => {
      if (weekIndex < 0 || dateRanges.length === 0) return -1;
      // Calculate reverse index (from oldest week, which is at the end of the array)
      const reverseIndex = dateRanges.length - 1 - weekIndex;
      // Find which 4-week period this week belongs to
      const period = Math.floor(reverseIndex / 4);
      // The baseline is the first week (oldest) of each period
      const baselineReverseIndex = period * 4;
      const baselineIndex = dateRanges.length - 1 - baselineReverseIndex;
      return baselineIndex;
    },
    [dateRanges.length]
  );

  // Check if the selected week is the baseline calculation week
  const isBaselineCalculationPeriod = useCallback(
    (startDate: string, endDate: string): boolean => {
      if (!startDate || !endDate || dateRanges.length === 0) return false;

      const weekIndex = getWeekIndex(startDate);
      if (weekIndex < 0) return false;

      const baselineWeekIndex = getBaselineWeekIndex(weekIndex);
      return weekIndex === baselineWeekIndex;
    },
    [dateRanges, getWeekIndex, getBaselineWeekIndex]
  );

  // Check if the selected week is within the validity period for its baseline
  const isWithinBaselineValidityPeriod = useCallback(
    (startDate: string, endDate: string): boolean => {
      if (!startDate || !endDate || dateRanges.length === 0) return false;

      const weekIndex = getWeekIndex(startDate);
      if (weekIndex < 0) return false;

      const baselineWeekIndex = getBaselineWeekIndex(weekIndex);
      if (baselineWeekIndex < 0) return false;

      // Calculate reverse indices to find period boundaries
      const reverseIndex = dateRanges.length - 1 - weekIndex;
      const period = Math.floor(reverseIndex / 4);

      // Period boundaries in reverse index space
      const periodStartReverseIndex = period * 4;
      const periodEndReverseIndex = Math.min((period + 1) * 4 - 1, dateRanges.length - 1);

      // Convert back to actual indices
      const periodStartIndex = dateRanges.length - 1 - periodEndReverseIndex;
      const periodEndIndex = dateRanges.length - 1 - periodStartReverseIndex;

      // Valid for all weeks in the same 4-week period
      return weekIndex >= periodStartIndex && weekIndex <= periodEndIndex;
    },
    [dateRanges, getWeekIndex, getBaselineWeekIndex]
  );

  // Get the baseline period info for a given week
  const getBaselinePeriod = useCallback(
    (
      startDate: string,
      endDate: string
    ): { baselineStart: string; baselineEnd: string; validStart: string; validEnd: string } | null => {
      if (!startDate || !endDate || dateRanges.length === 0) return null;

      const weekIndex = getWeekIndex(startDate);
      if (weekIndex < 0) return null;

      const baselineWeekIndex = getBaselineWeekIndex(weekIndex);
      if (baselineWeekIndex < 0 || baselineWeekIndex >= dateRanges.length) return null;

      const baselineWeek = dateRanges[baselineWeekIndex];

      // Calculate period boundaries to find validity end
      const reverseIndex = dateRanges.length - 1 - weekIndex;
      const period = Math.floor(reverseIndex / 4);
      const periodEndReverseIndex = Math.min((period + 1) * 4 - 1, dateRanges.length - 1);
      const validityEndIndex = dateRanges.length - 1 - periodEndReverseIndex;
      const validityEndWeek = dateRanges[validityEndIndex];

      return {
        baselineStart: baselineWeek.startDate,
        baselineEnd: baselineWeek.endDate,
        validStart: baselineWeek.startDate,
        validEnd: validityEndWeek.endDate,
      };
    },
    [dateRanges, getWeekIndex, getBaselineWeekIndex]
  );

  // Get tier baseline data
  const getTierBaselineData = useCallback(
    (tierName: string, locations: LocationData[]): TierBaselineData | null => {
      // Baseline is only for weekly view
      if (sortBy === 'dm' || viewMode !== 'weekly' || dateRanges.length === 0 || !selectedDateRange) {
        return null;
      }

      if (baselineDataByTier[tierName]) {
        return baselineDataByTier[tierName];
      }

      if (baselineLoading) {
        return null;
      }

      const weekIndex = getWeekIndex(selectedDateRange.startDate);
      if (weekIndex < 0) return null;

      const baselineWeekIndex = getBaselineWeekIndex(weekIndex);
      if (baselineWeekIndex < 0 || baselineWeekIndex >= dateRanges.length) return null;

      const baselineWeek = dateRanges[baselineWeekIndex];
      const baselineDateRange = baselineWeek
        ? `${formatDisplayDate(baselineWeek.startDate)} → ${formatDisplayDate(baselineWeek.endDate)}`
        : '';

      const isCalcPeriod = isBaselineCalculationPeriod(selectedDateRange.startDate, selectedDateRange.endDate);
      const isValidPeriod = isWithinBaselineValidityPeriod(selectedDateRange.startDate, selectedDateRange.endDate);

      let baseline: BaselineData | null = null;
      let baselineChanges: BaselineChanges | null = null;

      if (isCalcPeriod) {
        // Pass the end date of the baseline week and all date ranges to filter out locations that haven't existed for 8 ranges
        // IMPORTANT: When baseline week is the same as current week, use selectedDateRange.endDate to ensure consistency
        const endDateForFiltering = 
          baselineWeek.endDate === selectedDateRange.endDate 
            ? selectedDateRange.endDate  // Use current week's end date for consistency
            : baselineWeek.endDate;      // Otherwise use baseline week's end date
        
        console.log(`[useBaseline] Calculating baseline for tier ${tierName}, baseline week: ${baselineWeek.startDate} to ${baselineWeek.endDate}, locations count: ${locations.length}`);
        console.log(`[useBaseline] Using endDate for filtering: ${endDateForFiltering} (baselineWeek.endDate: ${baselineWeek.endDate}, selectedDateRange.endDate: ${selectedDateRange.endDate})`);
        baseline = calculateBaselineAverages(locations, undefined, endDateForFiltering, dateRanges);
        storeBaseline(tierName, baselineWeek, baselineWeekIndex, baseline);
      } else if (isValidPeriod) {
        console.log(`[useBaseline] Using stored baseline for tier ${tierName}, baseline week: ${baselineWeek.startDate} to ${baselineWeek.endDate}`);
        baseline = getStoredBaseline(tierName, baselineWeek, baselineWeekIndex);
        if (baseline) {
          console.log(`[useBaseline] Stored baseline found - this may be from before the filter was added. Consider clearing localStorage.`);
        }
      }

      if (!baseline) return null;

      // Calculate previous baseline week index correctly
      // Since dateRanges are sorted newest-first, we need to find the previous period's baseline
      const currentBaselineReverseIndex = dateRanges.length - 1 - baselineWeekIndex;
      const currentPeriod = Math.floor(currentBaselineReverseIndex / 4);
      const previousPeriod = currentPeriod - 1;
      
      let previousBaseline: BaselineData | null = null;
      if (previousPeriod >= 0) {
        // Previous period's baseline reverse index
        const previousBaselineReverseIndex = previousPeriod * 4;
        const previousBaselineWeekIndex = dateRanges.length - 1 - previousBaselineReverseIndex;
        
        if (previousBaselineWeekIndex >= 0 && previousBaselineWeekIndex < dateRanges.length) {
          const previousBaselineWeek = dateRanges[previousBaselineWeekIndex];
          previousBaseline = getStoredBaseline(tierName, previousBaselineWeek, previousBaselineWeekIndex);
        }
      }

      // Calculate baseline changes if previous baseline exists
      if (previousBaseline) {
        baselineChanges = {
          gfsPercentPoints: baseline.gfsPercent - previousBaseline.gfsPercent,
          weeklyTotalDelta:
            previousBaseline.weeklyTotal !== 0 ? baseline.weeklyTotal - previousBaseline.weeklyTotal : null,
          weeklyGfsDelta:
            previousBaseline.weeklyGfs !== 0 ? baseline.weeklyGfs - previousBaseline.weeklyGfs : null,
        };
      }

      return {
        baseline,
        baselineWeekIndex,
        baselineDateRange,
        baselineChanges,
      };
    },
    [
      baselineDataByTier,
      baselineLoading,
      selectedDateRange,
      sortBy,
      viewMode,
      dateRanges,
      getWeekIndex,
      getBaselineWeekIndex,
      isBaselineCalculationPeriod,
      isWithinBaselineValidityPeriod,
    ]
  );

  const buildBaselineMap = useCallback(
    (tiers: SalesTier[], baselineWeekIndex: number, baselineWeek: DateRange, baselineDateRange: string) => {
      const map: Record<string, TierBaselineData> = {};
      tiers.forEach((tier) => {
        // Pass the end date of the baseline week and date ranges to filter out locations that haven't existed for 8 ranges
        const baseline = calculateBaselineAverages(tier.locations, undefined, baselineWeek.endDate, dateRanges);
        const entry: TierBaselineData = {
          baseline,
          baselineWeekIndex,
          baselineDateRange,
          baselineChanges: null,
        };
        map[tier.tierName] = entry;
        storeBaseline(tier.tierName, baselineWeek, baselineWeekIndex, baseline);
      });
      return map;
    },
    [dateRanges]
  );

  const mergeBaselineChanges = useCallback((current: Record<string, TierBaselineData>, previous: Record<string, BaselineData>) => {
    const updated: Record<string, TierBaselineData> = {};
    Object.entries(current).forEach(([tierName, entry]) => {
      const prevBaseline = previous[tierName];
      if (!prevBaseline) {
        updated[tierName] = entry;
        return;
      }

      const changes: BaselineChanges = {
        gfsPercentPoints: entry.baseline.gfsPercent - prevBaseline.gfsPercent,
        weeklyTotalDelta:
          prevBaseline.weeklyTotal !== 0 ? entry.baseline.weeklyTotal - prevBaseline.weeklyTotal : null,
        weeklyGfsDelta:
          prevBaseline.weeklyGfs !== 0 ? entry.baseline.weeklyGfs - prevBaseline.weeklyGfs : null,
      };

      updated[tierName] = {
        ...entry,
        baselineChanges: changes,
      };
    });

    return updated;
  }, []);

  useEffect(() => {
    if (!selectedDateRange || sortBy === 'dm' || viewMode !== 'weekly' || dateRanges.length === 0) {
      setBaselineDataByTier({});
      setBaselineLoading(false);
      setBaselineChangeLoading(false);
      return;
    }

    const weekIndex = getWeekIndex(selectedDateRange.startDate);
    if (weekIndex < 0) {
      setBaselineDataByTier({});
      setBaselineLoading(false);
      setBaselineChangeLoading(false);
      return;
    }

    const baselineWeekIndex = getBaselineWeekIndex(weekIndex);
    if (baselineWeekIndex < 0 || baselineWeekIndex >= dateRanges.length) {
      setBaselineDataByTier({});
      setBaselineLoading(false);
      setBaselineChangeLoading(false);
      return;
    }

    const baselineWeek = dateRanges[baselineWeekIndex];
    const baselineDateRange = `${formatDisplayDate(baselineWeek.startDate)} → ${formatDisplayDate(baselineWeek.endDate)}`;

    const currentBaselineReverseIndex = dateRanges.length - 1 - baselineWeekIndex;
    const currentPeriod = Math.floor(currentBaselineReverseIndex / 4);
    const previousPeriod = currentPeriod - 1;
    const previousBaselineWeekIndex =
      previousPeriod >= 0 ? dateRanges.length - 1 - previousPeriod * 4 : -1;
    const previousBaselineWeek =
      previousBaselineWeekIndex >= 0 && previousBaselineWeekIndex < dateRanges.length
        ? dateRanges[previousBaselineWeekIndex]
        : null;

      let isCancelled = false;
    const requestId = ++latestBaselineRequestRef.current;

    const fetchBaselineData = async () => {
      setBaselineLoading(true);
      setBaselineChangeLoading(!!previousBaselineWeek);
      setBaselineDataByTier({});
      let baselineLoadedSuccessfully = false;

      try {
        const baselineResponse = await fetchWithRetry(() =>
          DashboardService.getDashboardData(baselineWeek.startDate, baselineWeek.endDate, sortBy)
        );

        if (isCancelled || latestBaselineRequestRef.current !== requestId) {
          return;
        }

        const newMap = buildBaselineMap(
          baselineResponse.salesTiers,
          baselineWeekIndex,
          baselineWeek,
          baselineDateRange
        );
        setBaselineDataByTier(newMap);
        baselineLoadedSuccessfully = true;
      } catch (error) {
        console.error('Failed to load baseline data', error);
        setBaselineDataByTier({});
      } finally {
        if (!isCancelled && latestBaselineRequestRef.current === requestId) {
          setBaselineLoading(false);
        }
      }

      if (!baselineLoadedSuccessfully || !previousBaselineWeek) {
        if (!isCancelled && latestBaselineRequestRef.current === requestId) {
          setBaselineChangeLoading(false);
        }
        return;
      }

      try {
        const previousResponse = await fetchWithRetry(() =>
          DashboardService.getDashboardData(previousBaselineWeek.startDate, previousBaselineWeek.endDate, sortBy)
        );

        if (isCancelled || latestBaselineRequestRef.current !== requestId) {
          return;
        }

        const previousMap: Record<string, BaselineData> = {};
        previousResponse.salesTiers.forEach((tier: SalesTier) => {
          // Pass the end date of the previous baseline week and date ranges to filter out locations that haven't existed for 8 ranges
          previousMap[tier.tierName] = calculateBaselineAverages(tier.locations, undefined, previousBaselineWeek.endDate, dateRanges);
        });

        setBaselineDataByTier((current) => mergeBaselineChanges(current, previousMap));
      } catch (error) {
        console.warn('Failed to load previous baseline data', error);
      } finally {
        if (!isCancelled && latestBaselineRequestRef.current === requestId) {
          setBaselineChangeLoading(false);
        }
      }
    };

    fetchBaselineData();

    return () => {
      isCancelled = true;
    };
  }, [
    selectedDateRange,
    sortBy,
    viewMode,
    dateRanges,
    getWeekIndex,
    getBaselineWeekIndex,
    buildBaselineMap,
    mergeBaselineChanges,
    fetchWithRetry,
  ]);

  return {
    getBaselinePeriod,
    getTierBaselineData,
    baselineLoading,
    baselineChangeLoading,
  };
};

